import Bugsnag from '@bugsnag/js';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';

import styles from './styles.module.css';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { ROUTE_NAMES } from '../../router/route-names';
import Button, { ButtonType } from '../Button';

interface SessionErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const SessionErrorFallback: React.FC<SessionErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate(`../${ROUTE_NAMES.selectEvse}`, { replace: true });
  };

  // Check if this is a recursion error
  const isRecursionError = error.message?.includes('Maximum call stack size exceeded') || error.name === 'RangeError';

  return (
    <div className={styles.errorContainer} role="alert">
      <div className={styles.errorContent}>
        <h2 className={styles.errorTitle}>
          {isRecursionError ? t('app.errors.recursion.title') : t('app.errors.session.title')}
        </h2>
        <p className={styles.errorMessage}>
          {isRecursionError ? t('app.errors.recursion.description') : t('app.errors.session.description')}
        </p>
        {process.env.NODE_ENV === 'development' && (
          <details className={styles.errorDetails}>
            <summary>Error Details</summary>
            <pre className={styles.errorStack}>{error.stack}</pre>
          </details>
        )}
        <div className={styles.errorActions}>
          <Button label={t('app.errors.try_again')} type={ButtonType.secondary} onClick={resetErrorBoundary} />
          <Button label={t('app.errors.go_home')} type={ButtonType.primary} onClick={handleGoHome} />
        </div>
      </div>
    </div>
  );
};

interface SessionErrorBoundaryProps {
  children: React.ReactNode;
}

const SessionErrorBoundary: React.FC<SessionErrorBoundaryProps> = ({ children }) => {
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={SessionErrorFallback}
      onError={(error, errorInfo) => {
        // Log the error for debugging
        console.error('Session Error Boundary caught an error:', error, errorInfo);

        // Report to monitoring service (Bugsnag v8 API)
        if (Bugsnag.isStarted()) {
          Bugsnag.notify(error, event => {
            event.context = 'SessionErrorBoundary';
            event.severity = 'error';
            event.addMetadata('errorInfo', { componentStack: errorInfo.componentStack });
            event.addMetadata('browser', {
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString(),
            });
          });
        }
      }}
      onReset={() => {
        navigate(`../${ROUTE_NAMES.selectEvse}`, { replace: true });
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default SessionErrorBoundary;
